package com.sddamico

import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.layout.HorizontalDividerStyle
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.theme.modifyStyleBase
import org.jetbrains.compose.web.css.*

val HeadingFontFamily = arrayOf("Playfair Display", "Georgia", "serif")
val BodyFontFamily = arrayOf("Inter", "-apple-system", "BlinkMacSystemFont", "Segoe UI", "Roboto", "sans-serif")

@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
    ctx.stylesheet.registerStyleBase("body") {
        Modifier.fontFamily(*BodyFontFamily).lineHeight(1.5).userSelect(UserSelect.None)
    }

    ctx.stylesheet.registerStyleBase("input, textarea, select") {
        Modifier.userSelect(UserSelect.Auto)
    }

    ctx.theme.modifyStyleBase(HorizontalDividerStyle) {
        Modifier.styleModifier { property("border-color", SitePalette.border.toString()) }
    }
}

val HeadlineTextStyle = CssStyle.base {
    Modifier
        .fontSize(2.5.cssRem)
        .fontWeight(FontWeight.Bold)
        .fontFamily(*HeadingFontFamily)
        .color(SitePalette.text)
}

val SubheadlineTextStyle = CssStyle.base {
    Modifier
        .fontSize(1.cssRem)
        .color(SitePalette.textSecondary)
}

val CardStyle = CssStyle.base {
    Modifier
        .backgroundColor(SitePalette.surface)
        .border(1.px, LineStyle.Solid, SitePalette.border)
        .borderRadius(8.px)
        .padding(1.5.cssRem)
}

val PrimaryButtonStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(SitePalette.primary)
            .color(Colors.White)
            .borderRadius(8.px)
            .padding(topBottom = 0.75.cssRem, leftRight = 1.5.cssRem)
            .fontWeight(FontWeight.SemiBold)
            .cursor(Cursor.Pointer)
            .transition(Transition.of("background-color", 150.ms))
    }
    hover {
        Modifier.backgroundColor(SitePalette.primaryHover)
    }
}

val GhostButtonStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Colors.Transparent)
            .color(SitePalette.textSecondary)
            .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
            .cursor(Cursor.Pointer)
            .transition(Transition.of("color", 150.ms))
    }
    hover {
        Modifier.color(SitePalette.text)
    }
}

val BadgeStyle = CssStyle.base {
    Modifier
        .borderRadius(9999.px)
        .padding(topBottom = 0.25.cssRem, leftRight = 0.75.cssRem)
        .fontSize(0.75.cssRem)
        .fontWeight(FontWeight.Medium)
}

val TabBarStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .gap(0.5.cssRem)
        .borderBottom(1.px, LineStyle.Solid, SitePalette.border)
        .padding(bottom = 0.px)
}

val TabItemStyle = CssStyle {
    base {
        Modifier
            .padding(topBottom = 0.75.cssRem, leftRight = 1.cssRem)
            .color(SitePalette.textMuted)
            .cursor(Cursor.Pointer)
            .fontWeight(FontWeight.Medium)
            .transition(Transition.of("color", 150.ms))
    }
    hover {
        Modifier.color(SitePalette.text)
    }
}

val MonoNumberStyle = CssStyle.base {
    Modifier
        .fontWeight(FontWeight.SemiBold)
        .styleModifier { property("font-variant-numeric", "tabular-nums") }
}

val CircleButtonVariant = ButtonStyle.addVariantBase {
    Modifier.padding(0.px).borderRadius(50.percent)
}

val UncoloredButtonVariant = ButtonStyle.addVariantBase {
    Modifier.setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
}
